<template>
  <section class="page" data-testid="page-config">
    <header class="page-header">
      <div><h1>配置</h1><p>支持热更新字段编辑，敏感字段由后端脱敏。</p></div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>
    <div class="metrics-grid">
      <MetricCard label="配置字段" :value="rows.length" />
      <MetricCard label="可热更新" :value="hotFieldCount" />
      <MetricCard label="需重启" :value="restartFieldCount" />
      <MetricCard label="当前匹配" :value="filteredRows.length" />
    </div>
    <p v-if="pageError" class="error-text">{{ pageError }}</p>
    <p v-if="lastSaveResult" class="save-result">{{ lastSaveResult }}</p>
    <div v-if="auth.security" class="panel">
      <h2>Web 安全</h2>
      <div class="metrics-grid">
        <MetricCard label="安全状态" :value="auth.security.status" />
        <MetricCard label="认证" :value="auth.security.admin_auth_enabled ? 'enabled' : 'disabled'" />
        <MetricCard label="绑定地址" :value="auth.security.host || '-'" />
        <MetricCard label="锁定" :value="auth.security.locked ? 'locked' : 'open'" />
      </div>
      <p>{{ auth.security.message }}</p>
    </div>
    <div class="panel">
      <h2>用户特征分析</h2>
      <div class="form-grid">
        <FormField label="启用"><input type="checkbox" v-model="userAnalysis.enabled" /></FormField>
        <FormField label="轻量统计"><input type="checkbox" v-model="userAnalysis.lightweight_enabled" /></FormField>
        <FormField label="LLM 分析"><input type="checkbox" v-model="userAnalysis.llm_enabled" /></FormField>
        <FormField label="Pipeline 间隔"><input type="number" v-model.number="userAnalysis.pipeline_interval_messages" /></FormField>
        <FormField label="Memory 间隔"><input type="number" v-model.number="userAnalysis.memory_interval_messages" /></FormField>
        <FormField label="样本条数"><input type="number" v-model.number="userAnalysis.sample_messages" /></FormField>
      </div>
      <FormField label="System Prompt"><textarea v-model="userAnalysis.system_prompt" /></FormField>
      <FormField label="分析 Prompt"><textarea v-model="userAnalysis.prompt" /></FormField>
      <button class="button primary" :disabled="saving" @click="saveUserAnalysis">{{ saving ? "保存中..." : "保存用户特征配置" }}</button>
    </div>
    <div class="panel">
      <h2>发送节奏</h2>
      <div class="form-grid">
        <FormField label="启用"><input type="checkbox" v-model="deliveryTiming.enabled" /></FormField>
        <FormField label="Dry-run 计划"><input type="checkbox" v-model="deliveryTiming.dry_run_include_plan" /></FormField>
        <FormField label="同会话队列"><input type="checkbox" v-model="deliveryTiming.target_queue_enabled" /></FormField>
        <FormField label="队列上限"><input type="number" min="1" v-model.number="deliveryTiming.max_queue_size_per_target" /></FormField>
        <FormField label="Stale 秒数"><input type="number" min="0" step="0.1" v-model.number="deliveryTiming.stale_after_seconds" /></FormField>
        <FormField label="Stale 跳过"><input type="checkbox" v-model="deliveryTiming.stale_skip_enabled" /></FormField>
        <FormField label="跳过优先级"><input v-model="staleSkipPrioritiesText" placeholder="normal,proactive,low" /></FormField>
        <FormField label="全局最小间隔"><input type="number" min="0" step="0.01" v-model.number="deliveryTiming.global_min_interval_seconds" /></FormField>
        <FormField label="会话最小间隔"><input type="number" min="0" step="0.01" v-model.number="deliveryTiming.per_target_min_interval_seconds" /></FormField>
        <FormField label="首条硬上限"><input type="number" min="0" step="0.1" v-model.number="deliveryTiming.adaptive.hard_cap_seconds" /></FormField>
      </div>
      <p v-if="deliveryTimingError" class="error-text">{{ deliveryTimingError }}</p>
      <button class="button primary" :disabled="saving" @click="saveDeliveryTiming">{{ saving ? "保存中..." : "保存发送节奏" }}</button>
    </div>
    <div class="panel">
      <h2>人格行为</h2>
      <div class="form-grid">
        <FormField label="在场感"><input type="checkbox" v-model="presence.enabled" /></FormField>
        <FormField label="Bot 别名"><input v-model="presenceAliasesText" placeholder="桂花乌龙茶, 小黄" /></FormField>
        <FormField label="最小回复概率"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.min_reply_probability" /></FormField>
        <FormField label="最大回复概率"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.max_reply_probability" /></FormField>
        <FormField label="喊名字加成"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.bot_name_boost" /></FormField>
        <FormField label="问句加成"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.direct_question_boost" /></FormField>
        <FormField label="高能加成"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.high_energy_boost" /></FormField>
        <FormField label="熟人加成"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.affinity_boost" /></FormField>
        <FormField label="信任加成"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.trusted_affinity_boost" /></FormField>
        <FormField label="刚发言冷却秒数"><input type="number" min="0" step="1" v-model.number="presence.recent_reply_cooldown_seconds" /></FormField>
        <FormField label="刚发言倍率"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.recent_reply_multiplier" /></FormField>
        <FormField label="插话倍率"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.interrupt_multiplier" /></FormField>
        <FormField label="喊名接话"><input type="checkbox" v-model="presence.interrupt_bypass_on_name_mention" /></FormField>
        <FormField label="兜底概率"><input type="number" min="0" max="1" step="0.01" v-model.number="presence.fallback_probability" /></FormField>
      </div>
      <p v-if="presenceError" class="error-text">{{ presenceError }}</p>
      <div class="form-grid">
        <FormField label="情绪状态"><input type="checkbox" v-model="emotion.enabled" /></FormField>
        <FormField label="消息更新情绪"><input type="checkbox" v-model="emotion.update_on_message" /></FormField>
        <FormField label="Prompt 注入"><input type="checkbox" v-model="emotion.prompt_enabled" /></FormField>
        <FormField label="风格修正"><input type="checkbox" v-model="emotion.style_modifiers_enabled" /></FormField>
        <FormField label="影响发送节奏"><input type="checkbox" v-model="emotion.delivery_timing_enabled" /></FormField>
      </div>
      <div class="form-grid">
        <FormField label="复读保护"><input type="checkbox" v-model="replyNovelty.enabled" /></FormField>
        <FormField label="Prompt 提醒"><input type="checkbox" v-model="replyNovelty.prompt_enabled" /></FormField>
        <FormField label="最近轮数"><input type="number" min="1" max="50" step="1" v-model.number="replyNovelty.recent_turns" /></FormField>
        <FormField label="最小字数"><input type="number" min="1" step="1" v-model.number="replyNovelty.min_chars" /></FormField>
        <FormField label="软相似阈值"><input type="number" min="0" max="1" step="0.01" v-model.number="replyNovelty.soft_similarity" /></FormField>
        <FormField label="硬相似阈值"><input type="number" min="0" max="1" step="0.01" v-model.number="replyNovelty.hard_similarity" /></FormField>
        <FormField label="硬重复跳过"><input type="checkbox" v-model="replyNovelty.skip_hard_duplicates" /></FormField>
      </div>
      <p v-if="replyNoveltyError" class="error-text">{{ replyNoveltyError }}</p>
      <button class="button primary" :disabled="saving || Boolean(presenceError || replyNoveltyError)" @click="savePersonalityBehavior">{{ saving ? "保存中..." : "保存人格行为" }}</button>
    </div>
    <div class="panel">
      <h2>主动发言</h2>
      <div class="form-grid">
        <FormField label="启用"><input type="checkbox" v-model="proactive.enabled" /></FormField>
        <FormField label="群号"><input v-model="proactiveGroupsText" placeholder="20001,20002" /></FormField>
        <FormField label="触发概率"><input type="number" min="0" max="1" step="0.01" v-model.number="proactive.probability" /></FormField>
        <FormField label="最小间隔分钟"><input type="number" min="1" step="1" v-model.number="proactive.min_interval_minutes" /></FormField>
        <FormField label="活跃消息阈值"><input type="number" min="1" step="1" v-model.number="proactive.activity_threshold" /></FormField>
        <FormField label="话题新鲜秒数"><input type="number" min="1" step="1" v-model.number="proactive.topic_freshness_seconds" /></FormField>
        <FormField label="Bot 冷却秒数"><input type="number" min="1" step="1" v-model.number="proactive.bot_cooldown_seconds" /></FormField>
        <FormField label="活跃窗口秒数"><input type="number" min="1" step="1" v-model.number="proactive.activity_window_seconds" /></FormField>
        <FormField label="检查间隔秒数"><input type="number" min="1" step="1" v-model.number="proactive.check_interval_seconds" /></FormField>
      </div>
      <p v-if="proactiveError" class="error-text">{{ proactiveError }}</p>
      <button class="button primary" :disabled="saving || Boolean(proactiveError)" @click="saveProactiveBehavior">{{ saving ? "保存中..." : "保存主动发言" }}</button>
    </div>
    <div class="panel">
      <h2>热更新字段</h2>
      <div class="toolbar">
        <input v-model="fieldQuery" placeholder="搜索路径或值" />
        <button class="button" :class="{ primary: fieldFilter === 'all' }" @click="fieldFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: fieldFilter === 'hot' }" @click="fieldFilter = 'hot'">热更新</button>
        <button class="button" :class="{ primary: fieldFilter === 'restart' }" @click="fieldFilter = 'restart'">需重启</button>
      </div>
      <DataTable :columns="columns" :rows="filteredRows">
        <template #hot="{ row }"><StatusBadge :status="row.hot ? 'hot' : 'restart'" /></template>
        <template #actions="{ row }"><button v-if="row.hot" class="button small" @click="openEdit(row)">编辑</button></template>
      </DataTable>
    </div>
    <Modal :open="Boolean(editing)" :title="editing ? `编辑 ${editing.path}` : '编辑配置'" @close="editing = null">
      <FormField label="值"><textarea v-model="editValue" /></FormField>
      <p class="hint-text">将按 {{ parsedEditType }} 保存</p>
      <p v-if="editError" class="error-text">{{ editError }}</p>
      <footer class="actions">
        <button class="button" @click="editing = null">取消</button>
        <button class="button primary" :disabled="saving" @click="saveEdit">{{ saving ? "保存中..." : "保存" }}</button>
      </footer>
    </Modal>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import Modal from "../ui/Modal.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";
import { useAuthStore } from "../stores/auth";

const toast = useToastStore();
const auth = useAuthStore();
const config = ref<Record<string, any>>({});
const hotPaths = ref<string[]>([]);
const userAnalysis = reactive<Record<string, any>>({});
const deliveryTiming = reactive<Record<string, any>>({
  enabled: true,
  dry_run_include_plan: true,
  target_queue_enabled: true,
  max_queue_size_per_target: 5,
  stale_after_seconds: 30,
  stale_skip_enabled: true,
  stale_skip_priorities: ["normal", "proactive", "low"],
  global_min_interval_seconds: 0.15,
  per_target_min_interval_seconds: 0.4,
  adaptive: { hard_cap_seconds: 6 }
});
const staleSkipPrioritiesText = ref("normal,proactive,low");
const proactive = reactive<Record<string, any>>({
  enabled: false,
  groups: [],
  min_interval_minutes: 15,
  activity_threshold: 3,
  probability: 0.3,
  topic_freshness_seconds: 600,
  bot_cooldown_seconds: 600,
  activity_window_seconds: 300,
  check_interval_seconds: 300
});
const proactiveGroupsText = ref("");
const presence = reactive<Record<string, any>>({
  enabled: true,
  bot_aliases: [],
  min_reply_probability: 0,
  max_reply_probability: 0.9,
  bot_name_boost: 0.18,
  direct_question_boost: 0.1,
  high_energy_boost: 0.06,
  affinity_boost: 0.06,
  trusted_affinity_boost: 0.12,
  recent_reply_cooldown_seconds: 45,
  recent_reply_multiplier: 0.55,
  interrupt_multiplier: 0.15,
  interrupt_bypass_on_name_mention: true,
  fallback_probability: 0.15
});
const presenceAliasesText = ref("");
const emotion = reactive<Record<string, any>>({
  enabled: true,
  update_on_message: true,
  prompt_enabled: true,
  style_modifiers_enabled: true,
  delivery_timing_enabled: true
});
const replyNovelty = reactive<Record<string, any>>({
  enabled: true,
  prompt_enabled: true,
  recent_turns: 8,
  min_chars: 8,
  soft_similarity: 0.82,
  hard_similarity: 0.94,
  skip_hard_duplicates: true
});
const loading = ref(false);
const saving = ref(false);
const pageError = ref("");
const lastSaveResult = ref("");
const editing = ref<any | null>(null);
const editValue = ref("");
const editError = ref("");
const fieldQuery = ref("");
const fieldFilter = ref<"all" | "hot" | "restart">("all");
const columns = [
  { key: "path", label: "路径" },
  { key: "display", label: "值" },
  { key: "hot", label: "热更新" },
  { key: "actions", label: "操作" }
];
function parseConfigInput(raw: string): { value: any; type: string } {
  const trimmed = raw.trim();
  try {
    const value = JSON.parse(raw);
    const type = Array.isArray(value) ? "array" : value === null ? "null" : typeof value;
    return { value, type };
  } catch {
    if (trimmed && !Number.isNaN(Number(trimmed))) return { value: Number(trimmed), type: "number" };
    if (trimmed === "true" || trimmed === "false") return { value: trimmed === "true", type: "boolean" };
    return { value: raw, type: "string" };
  }
}
function flatten(obj: Record<string, any>, prefix = "", out: any[] = []) {
  for (const [key, value] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (path === "user_analysis" || path.startsWith("user_analysis.")) continue;
    if (value && typeof value === "object" && !Array.isArray(value)) flatten(value, path, out);
    else {
      const hot = hotPaths.value.some((candidate) => path === candidate || path.startsWith(`${candidate}.`));
      const display = /api_key|token/i.test(path) ? "***" : JSON.stringify(value);
      out.push({ path, value, display, hot });
    }
  }
  return out;
}
const rows = computed(() => flatten(config.value));
const hotFieldCount = computed(() => rows.value.filter((row) => row.hot).length);
const restartFieldCount = computed(() => rows.value.length - hotFieldCount.value);
const filteredRows = computed(() => {
  const query = fieldQuery.value.trim().toLowerCase();
  return rows.value.filter((row) => {
    if (fieldFilter.value === "hot" && !row.hot) return false;
    if (fieldFilter.value === "restart" && row.hot) return false;
    if (!query) return true;
    return `${row.path} ${row.display}`.toLowerCase().includes(query);
  });
});
const parsedEditType = computed(() => parseConfigInput(editValue.value).type);
function parseGroupIds(raw: string) {
  const text = raw.trim();
  if (!text) return [];
  const values = text.split(/[,，\s]+/).map((part) => part.trim()).filter(Boolean);
  const ids = values.map((part) => Number(part));
  if (ids.some((value) => !Number.isInteger(value) || value < 0)) {
    throw new Error("群号必须是非负整数，可用逗号或空格分隔");
  }
  return ids;
}
function parseAliases(raw: string) {
  return raw
    .split(/[,，\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}
function parseDeliveryPriorities(raw: string) {
  const allowed = new Set(["immediate", "debug", "normal", "proactive", "low"]);
  const values = raw
    .split(/[,，\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const invalid = values.filter((value) => !allowed.has(value));
  if (invalid.length) {
    throw new Error(`未知优先级：${invalid.join(", ")}`);
  }
  return Array.from(new Set(values));
}
const deliveryTimingError = computed(() => {
  try {
    parseDeliveryPriorities(staleSkipPrioritiesText.value);
  } catch (err) {
    return err instanceof Error ? err.message : "跳过优先级格式错误";
  }
  const staleAfter = Number(deliveryTiming.stale_after_seconds);
  const hardCap = Number(deliveryTiming.adaptive?.hard_cap_seconds);
  if (Number.isFinite(staleAfter) && Number.isFinite(hardCap) && staleAfter < hardCap) {
    return "Stale 秒数不能小于首条硬上限";
  }
  return "";
});
const presenceError = computed(() => {
  const minProbability = Number(presence.min_reply_probability);
  const maxProbability = Number(presence.max_reply_probability);
  if (Number.isFinite(minProbability) && Number.isFinite(maxProbability) && minProbability > maxProbability) {
    return "最小回复概率不能大于最大回复概率";
  }
  return "";
});
const proactiveError = computed(() => {
  try {
    parseGroupIds(proactiveGroupsText.value);
  } catch (err) {
    return err instanceof Error ? err.message : "群号格式错误";
  }
  const probability = Number(proactive.probability);
  if (!Number.isFinite(probability) || probability < 0 || probability > 1) {
    return "触发概率必须在 0 到 1 之间";
  }
  return "";
});
const replyNoveltyError = computed(() => {
  const soft = Number(replyNovelty.soft_similarity);
  const hard = Number(replyNovelty.hard_similarity);
  if (!Number.isFinite(soft) || !Number.isFinite(hard) || soft < 0 || soft > 1 || hard < 0 || hard > 1) {
    return "复读保护阈值必须在 0 到 1 之间";
  }
  if (hard < soft) return "硬相似阈值不能小于软相似阈值";
  return "";
});
async function load() {
  loading.value = true;
  pageError.value = "";
  try {
    const payload = await api<{ config: Record<string, any>; hot_reload_paths: string[] }>("/api/config");
    config.value = payload.config || {};
    hotPaths.value = payload.hot_reload_paths || [];
    Object.assign(userAnalysis, config.value.user_analysis || {});
    Object.assign(deliveryTiming, config.value.behavior?.delivery_timing || {});
    deliveryTiming.adaptive = { ...(deliveryTiming.adaptive || {}), ...(config.value.behavior?.delivery_timing?.adaptive || {}) };
    staleSkipPrioritiesText.value = (deliveryTiming.stale_skip_priorities || ["normal", "proactive", "low"]).join(",");
    Object.assign(proactive, config.value.behavior?.proactive || {});
    proactiveGroupsText.value = (proactive.groups || []).join(",");
    Object.assign(presence, config.value.behavior?.presence || {});
    presenceAliasesText.value = (presence.bot_aliases || []).join(",");
    Object.assign(emotion, config.value.behavior?.emotion || {});
    Object.assign(replyNovelty, config.value.behavior?.reply_novelty || {});
  } catch (err) {
    pageError.value = errorText(err, "配置加载失败");
  } finally {
    loading.value = false;
  }
}
function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}
function describeSaveResult(label: string, result: any) {
  if (result?.requires_restart) return `${label}已保存，重启后生效`;
  if (result?.hot_reloaded) return `${label}已保存并热更新`;
  return `${label}已保存`;
}
async function runSave(label: string, operation: () => Promise<any>) {
  saving.value = true;
  pageError.value = "";
  lastSaveResult.value = "";
  try {
    const result = await operation();
    lastSaveResult.value = describeSaveResult(label, result);
    toast.push(lastSaveResult.value);
    await load();
    return result;
  } catch (err) {
    pageError.value = errorText(err, `${label}保存失败`);
    return null;
  } finally {
    saving.value = false;
  }
}
async function saveDeliveryTiming() {
  if (deliveryTimingError.value) return;
  await runSave("发送节奏", () => {
    const value = JSON.parse(JSON.stringify(deliveryTiming));
    value.stale_skip_priorities = parseDeliveryPriorities(staleSkipPrioritiesText.value);
    return postJson("/api/config/update", { path: "behavior.delivery_timing", value });
  });
}
async function savePersonalityBehavior() {
  if (presenceError.value || replyNoveltyError.value) return;
  await runSave("人格行为", () => {
    const behavior = {
      ...(config.value.behavior || {}),
      presence: {
        ...JSON.parse(JSON.stringify(presence)),
        bot_aliases: parseAliases(presenceAliasesText.value)
      },
      emotion: JSON.parse(JSON.stringify(emotion)),
      reply_novelty: JSON.parse(JSON.stringify(replyNovelty))
    };
    return postJson("/api/config/update", { path: "behavior", value: behavior });
  });
}
async function saveProactiveBehavior() {
  if (proactiveError.value) return;
  await runSave("主动发言", () => {
    const behavior = {
      ...(config.value.behavior || {}),
      proactive: {
        ...JSON.parse(JSON.stringify(proactive)),
        groups: parseGroupIds(proactiveGroupsText.value)
      }
    };
    return postJson("/api/config/update", { path: "behavior", value: behavior });
  });
}
async function saveUserAnalysis() {
  await runSave("用户特征配置", () => postJson("/api/config/update", { path: "user_analysis", value: { ...userAnalysis } }));
}
function openEdit(row: any) {
  editing.value = row;
  editValue.value = typeof row.value === "string" ? row.value : JSON.stringify(row.value, null, 2);
  editError.value = "";
}
async function saveEdit() {
  if (!editing.value) return;
  const parsed = parseConfigInput(editValue.value).value;
  editError.value = "";
  try {
    const result = await runSave(editing.value.path, () => postJson("/api/config/update", { path: editing.value.path, value: parsed }));
    if (result) editing.value = null;
    else editError.value = pageError.value;
  } catch (err) {
    editError.value = errorText(err, "保存失败");
  }
}
onMounted(load);
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
